import {
    Badge,
    Box,
    Button,
    Checkbox,
    Flex,
    Heading,
    HStack,
    Input,
    Select,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    useToast,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import {
    Waybill,
    WaybillStatus,
    useAssignDriverToRouteBatchMutation,
    useAssignDriverToWaybillMutation,
    useGetWaybillsQuery,
    useUpdateWaybillStatusMutation,
} from "../../orderApi/waybillApi";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

const cardsBorderRadii ="8px";

const statusOptions: Array<{ label: string; value: WaybillStatus | ""; }> = [
    { label: "All", value: "" },
    { label: "Pending Payment", value: "pending_payment" },
    { label: "Pending Dispatch", value: "pending_dispatch" },
    { label: "Driver Assigned", value: "driver_assigned" },
    { label: "Picked Up", value: "picked_up" },
    { label: "In Transit", value: "in_transit" },
    { label: "Delivered", value: "delivered" },
    { label: "Delivery Failed", value: "delivery_failed" },
    { label: "Returned", value: "returned" },
];

const nextStatusOptions: Array<{ label: string; value: WaybillStatus; }> = [
    { label: "Arrived at Pickup", value: "arrived_at_pickup" },
    { label: "Picked Up", value: "picked_up" },
    { label: "In Transit", value: "in_transit" },
    { label: "Arrived at Delivery", value: "arrived_at_delivery" },
    { label: "Delivered", value: "delivered" },
    { label: "Delivery Failed", value: "delivery_failed" },
    { label: "Return Started", value: "return_started" },
    { label: "Returned", value: "returned" },
    { label: "Cancelled", value: "cancelled" },
];

const getStatusColor = (status: string) => {
    if (status === "delivered") return "green";
    if (status === "pending_dispatch") return "orange";
    if (status === "driver_assigned") return "blue";
    if (status === "in_transit") return "purple";
    if (status === "cancelled" || status === "delivery_failed") return "red";
    return "gray";
};

const WaybillDispatchPage = () => {
    const { theme } = useCustomTheme();
    const toast = useToast();

    const [statusFilter, setStatusFilter] = useState<WaybillStatus | "">(
        "pending_dispatch",
    );

    const [selectedWaybillIds, setSelectedWaybillIds] = useState<string[]>([]);

    const [driverForm, setDriverForm] = useState({
        driverId: "",
        driverName: "",
        driverPhone: "",
    });

    const { data: waybills = [], isLoading, refetch } = useGetWaybillsQuery({
        status: statusFilter,
    });

    const [assignDriverToWaybill, { isLoading: assigningSingle }] =
        useAssignDriverToWaybillMutation();

    const [assignDriverToRouteBatch, { isLoading: assigningBatch }] =
        useAssignDriverToRouteBatchMutation();

    const [updateWaybillStatus, { isLoading: updatingStatus }] =
        useUpdateWaybillStatusMutation();

    const selectedWaybills = useMemo(
        () => waybills.filter((waybill) => selectedWaybillIds.includes(waybill._id)),
        [selectedWaybillIds, waybills],
    );

    const allVisibleSelected =
        waybills.length > 0 &&
        waybills.every((waybill) => selectedWaybillIds.includes(waybill._id));

    const toggleOne = (id: string) => {
        setSelectedWaybillIds((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
        );
    };

    const toggleAllVisible = () => {
        if (allVisibleSelected) {
            setSelectedWaybillIds([]);
            return;
        }

        setSelectedWaybillIds(waybills.map((waybill) => waybill._id));
    };

    const handleAssignSingle = async (waybill: Waybill) => {
        if (!driverForm.driverId.trim()) {
            toast({
                title: "Driver ID required",
                status: "warning",
                duration: 3000,
            });
            return;
        }

        try {
            await assignDriverToWaybill({
                id: waybill._id,
                driverId: driverForm.driverId,
                driverName: driverForm.driverName,
                driverPhone: driverForm.driverPhone,
            }).unwrap();

            toast({
                title: "Driver assigned",
                status: "success",
                duration: 2500,
            });

            refetch();
        } catch (error: any) {
            toast({
                title: "Failed to assign driver",
                description: error?.data?.message || error?.message,
                status: "error",
                duration: 4000,
            });
        }
    };

    const handleAssignBatch = async () => {
        if (!driverForm.driverId.trim()) {
            toast({
                title: "Driver ID required",
                status: "warning",
                duration: 3000,
            });
            return;
        }

        if (selectedWaybillIds.length === 0) {
            toast({
                title: "Select waybills first",
                status: "warning",
                duration: 3000,
            });
            return;
        }

        try {
            const result = await assignDriverToRouteBatch({
                waybillIds: selectedWaybillIds,
                driverId: driverForm.driverId,
                driverName: driverForm.driverName,
                driverPhone: driverForm.driverPhone,
            }).unwrap();

            toast({
                title: "Route batch created",
                description: `Batch ${result.routeBatchId} assigned to driver.`,
                status: "success",
                duration: 3500,
            });

            setSelectedWaybillIds([]);
            refetch();
        } catch (error: any) {
            toast({
                title: "Failed to assign route batch",
                description: error?.data?.message || error?.message,
                status: "error",
                duration: 4000,
            });
        }
    };

    const handleStatusChange = async (waybill: Waybill, status: WaybillStatus) => {
        try {
            await updateWaybillStatus({
                id: waybill._id,
                status,
            }).unwrap();

            toast({
                title: "Waybill updated",
                status: "success",
                duration: 2500,
            });

            refetch();
        } catch (error: any) {
            toast({
                title: "Failed to update waybill",
                description: error?.data?.message || error?.message,
                status: "error",
                duration: 4000,
            });
        }
    };

    return (
        <Box p={{ base: 4, md: 6 }} bg={theme.background} minH="100vh">
            <Box
                bg={theme.surface}
                border="1px solid"
                borderColor={theme.border}
                borderRadius={cardsBorderRadii}
                p={{ base: 4, md: 5 }}
                boxShadow={theme.shadowMd}
            >
                <Flex
                    justify="space-between"
                    align={{ base: "start", md: "center" }}
                    direction={{ base: "column", md: "row" }}
                    gap={4}
                    mb={5}
                >
                    <Box>
                        <Heading size="lg" color={theme.text}>
                            Waybill Dispatch
                        </Heading>
                        <Text color={theme.textMuted} mt={1}>
                            Assign drivers to seller waybills or group nearby waybills into one route batch.
                        </Text>
                    </Box>

                    <HStack>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as WaybillStatus | "")}
                            bg={theme.inputBg}
                            color={theme.text}
                            borderColor={theme.inputBorder}
                            maxW="220px"
                        >
                            {statusOptions.map((option) => (
                                <option key={option.value || "all"} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>

                        <Button onClick={() => refetch()}>Refresh</Button>
                    </HStack>
                </Flex>

                <Box
                    p={4}
                    borderRadius={cardsBorderRadii}
                    bg={theme.surfaceAlt}
                    border="1px solid"
                    borderColor={theme.border}
                    mb={5}
                >
                    <Heading size="sm" color={theme.text} mb={3}>
                        Driver Assignment
                    </Heading>

                    <Flex gap={3} direction={{ base: "column", md: "row" }}>
                        <Input
                            placeholder="Driver ID"
                            value={driverForm.driverId}
                            onChange={(e) =>
                                setDriverForm((prev) => ({ ...prev, driverId: e.target.value }))
                            }
                            bg={theme.inputBg}
                            color={theme.text}
                            borderColor={theme.inputBorder}
                        />

                        <Input
                            placeholder="Driver Name"
                            value={driverForm.driverName}
                            onChange={(e) =>
                                setDriverForm((prev) => ({ ...prev, driverName: e.target.value }))
                            }
                            bg={theme.inputBg}
                            color={theme.text}
                            borderColor={theme.inputBorder}
                        />

                        <Input
                            placeholder="Driver Phone"
                            value={driverForm.driverPhone}
                            onChange={(e) =>
                                setDriverForm((prev) => ({ ...prev, driverPhone: e.target.value }))
                            }
                            bg={theme.inputBg}
                            color={theme.text}
                            borderColor={theme.inputBorder}
                        />

                        <Button
                            color={theme.textOnPrimary}
                            bg={theme.primary}
                            _hover={{ bg: theme.primaryHover }}
                            onClick={handleAssignBatch}
                            isLoading={assigningBatch}
                            isDisabled={selectedWaybillIds.length === 0}
                            minW="190px"
                        >
                            Assign Route Batch
                        </Button>
                    </Flex>

                    {selectedWaybills.length > 0 && (
                        <Text color={theme.textMuted} fontSize="sm" mt={3}>
                            {selectedWaybills.length} waybill(s) selected.
                        </Text>
                    )}
                </Box>

                {isLoading ? (
                    <HStack p={6}>
                        <Spinner />
                        <Text color={theme.text}>Loading waybills...</Text>
                    </HStack>
                ) : (
                    <Box overflowX="auto">
                        <Table size="sm">
                            <Thead>
                                <Tr>
                                    <Th>
                                        <Checkbox
                                            isChecked={allVisibleSelected}
                                            onChange={toggleAllVisible}
                                        />
                                    </Th>
                                    <Th color={theme.textMuted}>Waybill</Th>
                                    <Th color={theme.textMuted}>Seller</Th>
                                    <Th color={theme.textMuted}>Pickup</Th>
                                    <Th color={theme.textMuted}>Dropoff</Th>
                                    <Th color={theme.textMuted}>Status</Th>
                                    <Th color={theme.textMuted}>Driver</Th>
                                    <Th color={theme.textMuted}>Actions</Th>
                                </Tr>
                            </Thead>

                            <Tbody>
                                {waybills.map((waybill) => (
                                    <Tr key={waybill._id}>
                                        <Td>
                                            <Checkbox
                                                isChecked={selectedWaybillIds.includes(waybill._id)}
                                                onChange={() => toggleOne(waybill._id)}
                                            />
                                        </Td>

                                        <Td>
                                            <VStack align="start" spacing={0}>
                                                <Text color={theme.text} fontWeight="700">
                                                    {waybill.waybillNumber}
                                                </Text>
                                                <Text color={theme.textMuted} fontSize="xs">
                                                    Package {waybill.packageIndex}/{waybill.packageCount}
                                                </Text>
                                            </VStack>
                                        </Td>

                                        <Td>
                                            <Text color={theme.text} fontWeight="600">
                                                {waybill.sellerName || waybill.sellerUserId}
                                            </Text>
                                            <Text color={theme.textMuted} fontSize="xs">
                                                {waybill.items?.length || 0} item(s)
                                            </Text>
                                        </Td>

                                        <Td maxW="240px">
                                            <Text color={theme.text} fontWeight="600">
                                                {waybill.pickupWarehouseName || "Warehouse"}
                                            </Text>
                                            <Text color={theme.textMuted} fontSize="xs" noOfLines={2}>
                                                {waybill.pickupLocation?.address}
                                            </Text>
                                        </Td>

                                        <Td maxW="240px">
                                            <Text color={theme.textMuted} fontSize="xs" noOfLines={2}>
                                                {waybill.dropoffLocation?.address}
                                            </Text>
                                        </Td>

                                        <Td>
                                            <Badge colorScheme={getStatusColor(waybill.status)} borderRadius={cardsBorderRadii}>
                                                {waybill.status.replace(/_/g, " ")}
                                            </Badge>
                                        </Td>

                                        <Td>
                                            <Text color={theme.text} fontWeight="600">
                                                {waybill.driverName || "—"}
                                            </Text>
                                            <Text color={theme.textMuted} fontSize="xs">
                                                {waybill.driverPhone || ""}
                                            </Text>
                                        </Td>

                                        <Td>
                                            <VStack align="stretch" spacing={2}>
                                                <Button
                                                    size="xs"
                                                    onClick={() => handleAssignSingle(waybill)}
                                                    isLoading={assigningSingle}
                                                >
                                                    Assign Driver
                                                </Button>

                                                <Select
                                                    size="xs"
                                                    placeholder="Update status"
                                                    onChange={(e) => {
                                                        const value = e.target.value as WaybillStatus;
                                                        if (value) handleStatusChange(waybill, value);
                                                    }}
                                                    isDisabled={updatingStatus}
                                                >
                                                    {nextStatusOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </VStack>
                                        </Td>
                                    </Tr>
                                ))}

                                {waybills.length === 0 && (
                                    <Tr>
                                        <Td colSpan={8}>
                                            <Box p={6}>
                                                <Text color={theme.textMuted}>No waybills found.</Text>
                                            </Box>
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default WaybillDispatchPage;